import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useRouter } from 'next/router';
import { comparator, sort } from 'ramda';

import { ArticleListItem } from '@definitions/articles.types';
import { ArchivedEditorialBlock } from '@components/blocks';
import { EditorialFilter } from '@components/editorialFilter';
import { FILTER_ITEMS, FILTER_TYPE, ITEMS_PER_PAGE } from '@components/editorialList/editorialList.constants';

import { Container, List } from './editorialList.styles';

export interface EditorialListProps {
  articles: ArticleListItem[];
}

export const EditorialList = ({ articles }: EditorialListProps) => {
  const [filteredArticles, setFilteredArticles] = useState<ArticleListItem[]>(articles);
  const [currentArticles, setCurrentArticles] = useState<ArticleListItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [lastIndex, setLastIndex] = useState<number>(-1);

  const router = useRouter();
  const [params, setParams] = useState<ParsedUrlQuery>(router.query);

  useEffect(() => {
    void router.push(
      {
        pathname: router.pathname,
        query: params,
      },
      undefined,
      { shallow: true }
    );
    const query = setQueryType(params.filter);
    filterArticles(query);
  }, [params]);

  useEffect(() => {
    setParams({ ...params, page: page.toString() });
  }, [page]);

  useEffect(() => {
    initialLoadArticles();
  }, [filteredArticles]);

  const initialLoadArticles = (perPage: number = ITEMS_PER_PAGE) => {
    if (filteredArticles.length > perPage) {
      setCurrentArticles(filteredArticles.slice(0, perPage));
      setLastIndex(perPage);
    } else {
      setCurrentArticles(filteredArticles.slice(0, filteredArticles.length));
      setLastIndex(filteredArticles.length);
    }
  };

  const loadArticles = (lastIndex: number, perPage: number = ITEMS_PER_PAGE) => {
    setCurrentArticles((curr) => [...curr, ...filteredArticles.slice(lastIndex, lastIndex + perPage)]);
    setLastIndex(lastIndex + perPage);
  };

  const filterArticles = (filter: FILTER_TYPE) => {
    if (filter === FILTER_TYPE.ALL) setFilteredArticles(articles);
    else setFilteredArticles(articles.filter((article) => article.category?.toLowerCase() === filter.toLowerCase()));
  };

  const setQueryType = (name: string | string[] | undefined): FILTER_TYPE => {
    switch (name) {
      case 'All':
        return FILTER_TYPE.ALL;
      case 'Profiles':
        return FILTER_TYPE.ART;
      case 'Culture':
        return FILTER_TYPE.CULTURE;
      case 'Service':
        return FILTER_TYPE.SERVICE;
      default:
        return FILTER_TYPE.ALL;
    }
  };

  const byDate = comparator(
    (a: ArticleListItem, b: ArticleListItem) =>
      new Date(b.firstPublishedAt).getTime() < new Date(a.firstPublishedAt).getTime()
  );

  return (
    <React.Fragment>
      <Container>
        <EditorialFilter items={FILTER_ITEMS} filterArticles={filterArticles} params={params} setParams={setParams} />
        <List>
          {sort(byDate, currentArticles).map((article, index) => {
            if (index % ITEMS_PER_PAGE === 0) {
              return (
                <InView
                  onChange={(entry) => {
                    if (entry) setPage(index / ITEMS_PER_PAGE + 1);
                  }}
                  key={index}
                >
                  <ArchivedEditorialBlock {...article} />
                </InView>
              );
            } else return <ArchivedEditorialBlock {...article} key={index} />;
          })}
          <InView
            onChange={(inView) => {
              if (inView) {
                loadArticles(lastIndex, ITEMS_PER_PAGE);
              }
            }}
          >
            <p>No more articles</p>
          </InView>
        </List>
      </Container>
    </React.Fragment>
  );
};
